import { useContext, useEffect } from 'react';
import PassContext from '../../../store/client/PassContext';
import PassTemplateContext from '../../../store/client/PassTemplateContext';
import { formatOptionLabelSelectBarcode } from '../../common/CustomReactSelect';
import {
  Box,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Hide,
  Input,
  Show,
  Switch,
} from '@chakra-ui/react';
import { CustomReactSelect } from '../../common/CustomReactSelect';

const PassesBarcodeForm = ({ colorModes, level }) => {
  const { modeBorderColor, modeFormControlBg, modeLabelBg } = colorModes;
  const ctx =
    level === 'pass'
      ? useContext(PassContext)
      : useContext(PassTemplateContext);

  const barcodeTypes = [
    { label: 'QR', value: 'qr' },
    { label: 'AZTEC', value: 'aztec' },
    { label: 'PDF417', value: 'pdf417' },
    { label: 'CODE128', value: 'code128' },
  ];

  const barcodeSources = [
    { label: 'UUID', value: 'uuid' },
    { label: 'Ext ID', value: 'extId' },
    { label: 'Custom', value: 'custom' },
  ];

  useEffect(() => {
    if (ctx?.barcode?.source === 'custom' && ctx?.barcode?.value === '') {
      ctx.updateBarcode({
        ...ctx?.barcode,
        value: '',
      });
    }
  }, [ctx?.barcode?.source]);

  return (
    <Box>
      <Divider borderColor={modeBorderColor} />
      <FormControl bg={modeFormControlBg}>
        <Flex alignItems="center">
          <Hide below="2xl">
            <Box pl="24px" flexShrink={0} w="240px">
              <FormLabel htmlFor="barcodeEnabled" m={0}>
                Enable Barcode
              </FormLabel>
            </Box>
          </Hide>
          <Box className="inner-box" p="14px" w="full" bg={modeLabelBg}>
            <Show below="2xl">
              <FormLabel htmlFor="barcodeEnabled">Enable Barcode</FormLabel>
            </Show>
            <Flex direction="row" gap={3} alignItems="center" ml={3}>
              <Switch
                id="barcodeEnabled"
                isChecked={ctx?.barcode?.enabled}
                onChange={() => {
                  ctx.updateBarcode({
                    ...ctx?.barcode,
                    enabled: !ctx?.barcode?.enabled,
                  });
                }}
              />
            </Flex>
          </Box>
        </Flex>
        <Flex alignItems="center">
          <Hide below="2xl">
            <Box pl="24px" flexShrink={0} w="240px">
              <FormLabel htmlFor="barcodeType" id="barcodeTypeLabel" m={0}>
                Barcode Type
              </FormLabel>
            </Box>
          </Hide>
          <Box className="inner-box" p="14px" w="full" bg={modeLabelBg}>
            <Show below="2xl">
              <FormLabel htmlFor="barcodeType" id="barcodeTypeSmLabel">
                Barcode Type
              </FormLabel>
            </Show>
            <Flex direction="column" gap={3}>
              <CustomReactSelect
                options={barcodeTypes}
                placeholder={
                  ctx?.barcode?.type
                    ? ctx?.barcode?.type?.toUpperCase()
                    : 'Select barcode type'
                }
                menuPlacement="auto"
                formatOptionLabel={formatOptionLabelSelectBarcode}
                onChange={(barcodeType) => {
                  ctx.updateBarcode({
                    ...ctx?.barcode,
                    type: barcodeType.value,
                  });
                }}
                value={barcodeTypes.find(
                  (type) => type.value === ctx?.barcode?.type
                )}
                isDisabled={!ctx?.barcode?.enabled}
                id="barcodeType"
                inputId="barcodeType"
                aria-label="Barcode Type"
              />
            </Flex>
          </Box>
        </Flex>
        <Flex alignItems="center">
          <Hide below="2xl">
            <Box pl="24px" flexShrink={0} w="240px">
              <FormLabel htmlFor="barcodeSource" m={0}>
                Barcode Source
              </FormLabel>
            </Box>
          </Hide>
          <Box className="inner-box" p="14px" w="full" bg={modeLabelBg}>
            <Show below="2xl">
              <FormLabel htmlFor="barcodeSource">Barcode Source</FormLabel>
            </Show>
            <Flex direction="column" gap={3}>
              <CustomReactSelect
                options={barcodeSources}
                placeholder={
                  ctx?.barcode?.source
                    ? ctx?.barcode?.source
                    : 'Select barcode source'
                }
                menuPlacement="auto"
                formatOptionLabel={formatOptionLabelSelectBarcode}
                onChange={(barcodeSource) => {
                  ctx.updateBarcode({
                    ...ctx?.barcode,
                    source: barcodeSource.value,
                    value:
                      barcodeSource.value === 'custom'
                        ? ctx?.barcode?.value
                        : level === 'pass' && barcodeSource.value === 'extId'
                          ? ctx?.externalId
                          : level === 'pass' && barcodeSource.value === 'uuid'
                            ? ctx?.passState?.uuid
                            : '',
                  });
                }}
                value={barcodeSources.find(
                  (source) => source.value === ctx?.barcode?.source
                )}
                isDisabled={!ctx?.barcode?.enabled}
                id="barcodeSource"
                inputId="barcodeSource"
                aria-label="Barcode Source"
              />
            </Flex>
          </Box>
        </Flex>
        <Flex alignItems="center">
          <Hide below="2xl">
            <Box pl="24px" flexShrink={0} w="240px">
              <FormLabel htmlFor="barcodeValue" m={0}>
                Barcode value
              </FormLabel>
            </Box>
          </Hide>
          <Box className="inner-box" p="14px" w="full" bg={modeLabelBg}>
            <Show below="2xl">
              <FormLabel htmlFor="barcodeValue">Barcode value</FormLabel>
            </Show>
            <Flex direction="column" gap={3}>
              <Input
                variant="filledForDarkBg"
                id="barcodeValue"
                type="text"
                placeholder={
                  ctx?.barcode?.source === 'custom'
                    ? 'Type here'
                    : ctx?.barcode?.source === 'uuid'
                      ? 'Pass UUID'
                      : ctx?.barcode?.source === 'extId'
                        ? 'Pass Ext ID (must be set)'
                        : 'Type here'
                }
                value={
                  ctx?.barcode?.source === 'custom'
                    ? ctx?.barcode?.value || ''
                    : ctx?.barcode?.source === 'extId'
                      ? ctx?.externalId || 'Pass Ext ID (Must be set)'
                      : ctx?.passState?.uuid || ''
                }
                onChange={(e) => {
                  if (ctx?.barcode?.source === 'custom') {
                    ctx.updateBarcode({
                      ...ctx?.barcode,
                      value: e.target.value,
                    });
                  }
                }}
                onBlur={() => {
                  const value = ctx?.barcode?.value;
                  if (value?.trim() === '') {
                    ctx.updateBarcode({
                      ...ctx?.barcode,
                      value: '',
                    });
                  }
                }}
                autoComplete="off"
                isDisabled={
                  ctx?.barcode?.enabled
                    ? ctx?.barcode?.source !== 'custom'
                    : true
                }
                opacity={ctx?.barcode?.source !== 'custom' ? 0.6 : 1}
              />
            </Flex>
          </Box>
        </Flex>
        <Flex alignItems="center">
          <Hide below="2xl">
            <Box pl="24px" flexShrink={0} w="240px">
              <FormLabel
                htmlFor="barcodeDisplayText"
                id="barcodeDisplayTextLabel"
                m={0}
              >
                Display Barcode value
              </FormLabel>
            </Box>
          </Hide>
          <Box className="inner-box" p="14px" w="full" bg={modeLabelBg}>
            <Show below="2xl">
              <FormLabel
                htmlFor="barcodeDisplayText"
                id="barcodeDisplayTextSmLabel"
              >
                Display Barcode value
              </FormLabel>
            </Show>
            <Flex direction="row" gap={3} alignItems="center" ml={3}>
              <Switch
                isRequired={false}
                id="barcodeDisplayText"
                aria-label="Display Barcode value"
                isChecked={ctx?.barcode?.displayText}
                onChange={() => {
                  ctx.updateBarcode({
                    ...ctx?.barcode,
                    displayText: !ctx?.barcode?.displayText,
                  });
                }}
                disabled={
                  ctx?.barcode?.enabled
                    ? ctx?.barcode?.type === undefined
                    : true
                }
              />
            </Flex>
          </Box>
        </Flex>
      </FormControl>
    </Box>
  );
};

export default PassesBarcodeForm;
